import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/admin/ProtectedRoute';
import Navbar from './components/Navbar';
import WhatsAppButton from './components/WhatsAppButton';
import { testBackend, testCORS, apiTester } from './utils/apiTest';
import NotFound from './components/NotFound';
import BackgroundManager from './components/BackgroundManager';
import { PageLoader } from './components/ui/PageLoader';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import './App.css';

// Lazy loading de páginas principales
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const UserHome = lazy(() => import('./pages/UserHome'));
const AdminHome = lazy(() => import('./pages/AdminHome'));
const MarketPage = lazy(() => import('./pages/MarketPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PublicProfile = lazy(() => import('./pages/PublicProfile/PublicProfile'));

// Lazy loading de managers/admin tools
const LocalesManager = lazy(() => import('./components/sectionsManagers/LocalesManager'));

// Lazy loading de páginas secundarias
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const InfiniteHome = lazy(() => import('./pages/InfiniteHome'));
const AIPage = lazy(() => import('./pages/AIPage'));
const AiChat = lazy(() => import('./pages/AiChat'));
const ChatBotPage = lazy(() => import('./pages/ChatBotPage'));
const ImageGenPage = lazy(() => import('./pages/ImageGenPage'));
const VideoGenPage = lazy(() => import('./pages/VideoGenPage'));
const AudioGenPage = lazy(() => import('./pages/AudioGenPage'));
const ThreeDGenPage = lazy(() => import('./pages/ThreeDGenPage'));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage'));
const VideoGalleryPage = lazy(() => import('./pages/VideoGalleryPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const GameDetailPage = lazy(() => import('./pages/GameDetailPage'));
const IntroForo = lazy(() => import('./pages/IntroForo'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const UserStatsPage = lazy(() => import('./pages/UserStatsPage'));
const TestsPage = lazy(() => import('./pages/TestsPage'));
const ComponentTesting = lazy(() => import('./pages/ComponentTesting'));
const DeliveryPage = lazy(() => import('./pages/DeliveryPage'));
const LocalesPage = lazy(() => import('./pages/LocalesPage'));
const LocalesGoogleMaps = lazy(() => import('./pages/LocalesGoogleMaps'));
const ComunidadPage = lazy(() => import('./pages/ComunidadPage/ComunidadPage'));
const CreatePostPage = lazy(() => import('./pages/ComunidadPage/pages/CreatePostPage'));
const EventosPage = lazy(() => import('./pages/EventosPage/EventosPage'));
const TransportePage = lazy(() => import('./pages/TransportePage/TransportePage'));
const AlojamientoPage = lazy(() => import('./pages/AlojamientoPage/AlojamientoPage'));

// Lazy loading de páginas de prueba (solo para admin)
const TestHomePages = lazy(() => import('./pages/TestHomePages'));
// AdminClone removed - was a test page
// AdminCloneMagicBento removed - was a test page
const TemplateTestPage = lazy(() => import('./pages/TemplateTestPage'));
const MarketTestPage = lazy(() => import('./pages/MarketTestPage'));
const ProductManagement = lazy(() => import('./pages/ProductManagement'));

// Lazy loading de versiones de home (solo para admin)
const HomePageV1 = lazy(() => import('./pages/HomePageV1'));
const HomePageV2 = lazy(() => import('./pages/HomePageV2'));
const HomePageV3 = lazy(() => import('./pages/HomePageV3'));
const HomePageV4 = lazy(() => import('./pages/HomePageV4'));
const HomePageV5 = lazy(() => import('./pages/HomePageV5'));
const HomePageV6 = lazy(() => import('./pages/HomePageV6'));
const HomePageV7 = lazy(() => import('./pages/HomePageV7'));
const HomePageV8 = lazy(() => import('./pages/HomePageV8'));
const HomePageV9 = lazy(() => import('./pages/HomePageV9'));
const HomePageV10 = lazy(() => import('./pages/HomePageV10'));
const HomePageV12 = lazy(() => import('./pages/HomePageV12'));

// Lazy loading de páginas V2.0 (solo para admin)
const LandingPageV2 = lazy(() => import('./pages/v2/LandingPageV2'));
const ProfileBuilderV2 = lazy(() => import('./pages/v2/ProfileBuilderV2'));
const PublicProfileEditor = lazy(() => import('./pages/v2/PublicProfileEditor'));
const AdvancedProfilePrototype = lazy(() => import('./pages/v2/Prototype/AdvancedProfilePrototype'));
const OnboardingV2 = lazy(() => import('./pages/v2/OnboardingV2'));
const DashboardV2 = lazy(() => import('./pages/v2/DashboardV2'));
const AdminCombinedDashboard = lazy(() => import('./pages/AdminCombinedDashboard'));
const LandingPageExperimental = lazy(() => import('./pages/LandingPageExperimental'));
// Landing para invitados (eliminado)

// Componente interno para manejar la lógica del navbar
function AppContent() {
  const location = useLocation();

  // Páginas donde SÍ debe mostrarse el navbar (oculto por defecto en el resto)
  const pagesWithNavbar = [
    '/user-home',
    '/dashboard',
    '/stats',
    '/messaging',
    '/market',
    '/services',
    '/projects',
    '/team',
    '/contact',
    '/forum',
    '/games',
    '/videogallery',
    '/ai',
    '/aitools',
    '/aichat',
    '/chatbot',
    '/imagegen',
    '/videogen',
    '/audiogen',
    '/3dgen',
    '/tests',
    '/component-testing',
    '/onboarding',
    '/v2/public-profile-editor',
    '/v2/profile-builder'
  ];

  // Páginas de admin que requieren navbar
  const adminPagesWithNavbar = [
    '/admin',
    '/products',
    '/market-test'
  ];

  // Verificar si la ruta actual requiere navbar
  const showNavbar = pagesWithNavbar.some(page => location.pathname === page) ||
                     location.pathname.startsWith('/games/') ||
                     location.pathname.startsWith('/admin/') ||
                     adminPagesWithNavbar.some(page => location.pathname.startsWith(page));

  // Debug: mostrar estado del navbar en desarrollo
  if (process.env.NODE_ENV === 'development') {
    console.log('🧭 Navbar Debug:', {
      path: location.pathname,
      showNavbar,
      isHomePage: location.pathname === '/',
      isAboutPage: location.pathname === '/about',
      isPublicProfilePage: location.pathname.startsWith('/@') && !location.pathname.includes('/v2/')
    });
  }

  return (
    <ErrorBoundary>
      <div className="App min-h-screen text-white overflow-x-hidden">
        <BackgroundManager backgroundType="both" showControls={false} />
        <RoleBasedRedirect />
        {showNavbar && <Navbar />}
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <Suspense fallback={<PageLoader message="Cargando página..." />}>
            <Routes>
              {/* Páginas principales con lazy loading */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/user-home" element={<UserHome />} />
              <Route path="/admin-home" element={
                <ProtectedRoute requireAdmin={true}>
                  <Navigate to="/admin/combined" replace />
                </ProtectedRoute>
              } />
              <Route path="/market" element={<MarketPage />} />
              <Route path="/profile/:userId" element={<PublicProfile />} />
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/stats" element={<UserStatsPage />} />
              
              {/* Páginas secundarias */}
              <Route path="/sobre-nosotros" element={<InfiniteHome />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/delivery" element={<DeliveryPage />} />
              <Route path="/locales" element={<LocalesPage />} />
              <Route path="/comunidad" element={<ComunidadPage />} />
              <Route path="/comunidad/crear-post" element={<CreatePostPage />} />
              <Route path="/eventos" element={<EventosPage />} />
              <Route path="/transporte" element={<TransportePage />} />
              <Route path="/alojamiento" element={<AlojamientoPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/ai" element={<AIPage />} />
              <Route path="/aitools" element={<AIToolsPage />} />
              <Route path="/aichat" element={<AiChat />} />
              <Route path="/chatbot" element={<ChatBotPage />} />
              <Route path="/imagegen" element={<ImageGenPage />} />
              <Route path="/videogen" element={<VideoGenPage />} />
              <Route path="/audiogen" element={<AudioGenPage />} />
              <Route path="/3dgen" element={<ThreeDGenPage />} />
              <Route path="/videogallery" element={<VideoGalleryPage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/:gameId" element={<GameDetailPage />} />
              {/* Ruta /landing eliminada */}
              <Route path="/forum" element={<IntroForo />} />
              <Route path="/messaging" element={<MessagingPage />} />
              <Route path="/tests" element={<TestsPage />} />
              <Route path="/component-testing" element={<ComponentTesting />} />

              {/* Onboarding - Accesible para usuarios nuevos */}
              <Route path="/onboarding" element={<OnboardingV2 />} />
              {/* Profile Builder V2 - Ruta pública para onboarding/perfil */}
              <Route path="/v2/profile-builder" element={<ProfileBuilderV2 />} />
              {/* Public Profile Editor - Editor de perfil público */}
              <Route path="/v2/public-profile-editor" element={<PublicProfileEditor />} />
              {/* Advanced Profile Prototype - Prototipo de perfiles avanzados */}
              <Route path="/v2/prototype/:profileName" element={<AdvancedProfilePrototype />} />
              
              {/* Páginas protegidas de admin */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/combined" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminCombinedDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requireAdmin={true}>
                  <UserManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/locales-manager" element={
                <ProtectedRoute requireAdmin={true}>
                  <LocalesManager />
                </ProtectedRoute>
              } />
              <Route path="/admin/locales-google-maps" element={
                <ProtectedRoute requireAdmin={true}>
                  <LocalesGoogleMaps />
                </ProtectedRoute>
              } />
              
              {/* Rutas de prueba protegidas - Solo accesibles por admin */}
              <Route path="/admin/test" element={
                <ProtectedRoute>
                  <TestHomePages />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/admin-clone" element={
                <ProtectedRoute>
                  {/* AdminClone removed - was a test page */}
                  <div />
                </ProtectedRoute>
              } />
              {/* AdminCloneMagicBento removed - was a test page */}
              <Route path="/market-test" element={
                <ProtectedRoute>
                  <MarketTestPage />
                </ProtectedRoute>
              } />
              <Route path="/products" element={
                <ProtectedRoute>
                  <ProductManagement />
                </ProtectedRoute>
              } />
              
              {/* Versiones de home para pruebas (solo admin) */}
              <Route path="/admin/test/home-v1" element={
                <ProtectedRoute>
                  <HomePageV1 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v2" element={
                <ProtectedRoute>
                  <HomePageV2 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v3" element={
                <ProtectedRoute>
                  <HomePageV3 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v4" element={
                <ProtectedRoute>
                  <HomePageV4 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v5" element={
                <ProtectedRoute>
                  <HomePageV5 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v6" element={
                <ProtectedRoute>
                  <HomePageV6 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v7" element={
                <ProtectedRoute>
                  <HomePageV7 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v8" element={
                <ProtectedRoute>
                  <HomePageV8 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v9" element={
                <ProtectedRoute>
                  <HomePageV9 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v10" element={
                <ProtectedRoute>
                  <HomePageV10 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v11" element={
                <ProtectedRoute>
                  <LandingPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/home-v12" element={
                <ProtectedRoute>
                  <HomePageV12 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/introforo" element={
                <ProtectedRoute>
                  <IntroForo />
                </ProtectedRoute>
              } />
              
              {/* Rutas V2.0 - Páginas de prueba (solo admin) */}
              <Route path="/admin/test/v2-landing" element={
                <ProtectedRoute>
                  <LandingPageV2 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/v2-profile-builder" element={
                <ProtectedRoute>
                  <ProfileBuilderV2 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/v2-onboarding" element={
                <ProtectedRoute>
                  <OnboardingV2 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/v2-dashboard" element={
                <ProtectedRoute>
                  <DashboardV2 />
                </ProtectedRoute>
              } />
              <Route path="/admin/test/landing-experimental" element={
                <ProtectedRoute>
                  <LandingPageExperimental />
                </ProtectedRoute>
              } />

              {/* Public Profiles - Rutas públicas para perfiles de usuario */}
              <Route path="/:profileName" element={<PublicProfile />} />

              {/* 404 - Página no encontrada */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </motion.main>
       
      </div>
    </ErrorBoundary>
  );
}

function App() {
  // Exponer funciones de diagnóstico en la consola del navegador
  React.useEffect(() => {
    (window as any).testBackend = testBackend;
    (window as any).testCORS = testCORS;
    (window as any).apiTester = apiTester;
    
    console.log('🔧 Funciones de diagnóstico disponibles en la consola:');
    console.log('• testBackend() - Ejecutar todas las pruebas');
    console.log('• testCORS() - Probar solo CORS');
    console.log('• apiTester - Instancia del tester para uso avanzado');
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <AppContent />
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
